import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AppIcon from "../../Common/AppIcon";
import {
  convertGmtToOffest,
  getBrowserGMTOffset,
  hasRoles,
  kindOf,
  showToast,
} from "../../../lib/utility";
import { getDatesFromDay } from "../../../lib/utils";
import {
  getReportDateRange,
  convertDateTimeLocalToReport,
  convertReportToDateTimeLocal,
  addTimeInReportDates,
  getLocalDateOffset,
  getReportEndDateMin,
} from "../../../Wrapper/ReportDateUtils";
import timeOptions, { countries } from "../../../Wrapper/GmtTimeData";
import { authSelector } from "../../Auth/AuthSlice";
import CloseButton from "../../Common/CloseButton";
import ReportDateTimePicker from "../../Common/ReportDateTimePicker";
import FormEnter from "../../Common/FormEnter";
import Modal from "../../Common/Modal";
import Select from "../../Common/Select";
import UserFilter from "../../Common/UserFilter";

const getDefaultFilterValues = () => {
  const dateRange = getReportDateRange(10);
  return {
    gmtOffset: getBrowserGMTOffset(),
    startDate: convertReportToDateTimeLocal(dateRange.startDate),
    endDate: convertReportToDateTimeLocal(dateRange.endDate),
    time: null,
    month: "",
    date: "",
    senderId: "",
    status: "%",
    country: "%",
  };
};

const getInitialFilterValues = (initialFilterData) => {
  const dateRange = getReportDateRange(10);
  return {
    gmtOffset: initialFilterData?.toGmt || getBrowserGMTOffset(),
    startDate: initialFilterData?.startdate
      ? convertReportToDateTimeLocal(initialFilterData.startdate)
      : convertReportToDateTimeLocal(dateRange.startDate),
    endDate: initialFilterData?.enddate
      ? convertReportToDateTimeLocal(initialFilterData.enddate)
      : convertReportToDateTimeLocal(dateRange.endDate),
    time: initialFilterData?.reportType || null,
    month: "",
    date: "",
    senderId: initialFilterData?.senderId || "",
    status: initialFilterData?.messageStatus || "%",
    country: initialFilterData?.country || "%",
  };
};

export default function SubmissionFilter({
  modal,
  closeModal,
  onApplyFilter,
  canSelectUsers,
  initialFilterData,
}) {
  const { t } = useTranslation(["submissionReport", "common"]);

  const [filterData, setFilterData] = useState(() =>
    getInitialFilterValues(initialFilterData)
  );
  const [daySelector, setDaySelector] = useState("10");

  const { user } = useSelector(authSelector);
  const isSupers =
    canSelectUsers === undefined
      ? hasRoles("admin,tenant,superadmin,system,seller,manager", user) &&
        window.location.pathname.includes("reports")
      : canSelectUsers;

  const [selectUsers, setSelectUsers] = useState({
    allowed: isSupers,
    userIds: "",
    next: false,
  });

 const getFilterObj = () => {
  const filterObj = {
    status: filterData.status,
    time: filterData.time,
    date: filterData.date,
    country: filterData.country,
    month: filterData.month,
    senderId: filterData.senderId || "",
    ...(isSupers ? { clientId: selectUsers.userIds || "%" } : {}),
  };
  
  if (filterData.time === null) {
    filterObj.startdate = convertDateTimeLocalToReport(filterData.startDate, false);
    filterObj.enddate = convertDateTimeLocalToReport(filterData.endDate, false);
  }
  
  return addTimeInReportDates(
    filterObj,
    convertGmtToOffest(filterData.gmtOffset)
  );
};
  const handleApplyClick = () => {
    if (filterData.time === "Daily") {
      if (filterData.date === "") {
        showToast("Please select the date",
          "warning"
        );
        return;
      }
    } else if (filterData.time === "Monthly") {
      if (filterData.month === "") {
        showToast("Please select the date",
          "warning"
        );
        return;
      }
    } else {
      if (filterData.startDate === "" || filterData.endDate === "") {
        showToast("Please select Start and End date",
          "warning"
        );
        return;
      }
    }

    kindOf(onApplyFilter, "function") && onApplyFilter(getFilterObj());
    modalClose();
  };
  const handleResetClick = () => {
    setFilterData(getDefaultFilterValues());
    setDaySelector("10");
  };
  const handleFilterSubmit = () => {
    handleApplyClick();
  };
  const handleTimeChange = (e) => {
    let time = e.target.value;
    setFilterData((prev) => ({
      ...prev,
      time: time === "Custom" ? null : time,
    }));
  };
  const handleFilterDataChange = (e) => {
    const { value, id } = e.target;

    setFilterData((prev) => ({
      ...prev,
      ...(id === "campaign" && value !== ""
        ? { checkA: "campaign" }
        : { checkA: "multiple" }),
      ...(id === "checkA" ? { campaign: "" } : {}),
      [id]: value,
      ...(filterData.campaign === "" &&
      (id === "checkA" || id === "campaign") &&
      value === ""
        ? { checkA: "multiple" }
        : {}),
    }));
  };
  const modalClose = () => {
    setSelectUsers({
      allowed: isSupers,
      userIds: "",
      next: false,
    });
    closeModal();
  };
  const handleDayChange = (e) => {
    let value = e.target.value;
    setDaySelector(value);

    let { startDate, endDate } = getDatesFromDay(value);

    if (startDate && !startDate.includes(" ")) {
      startDate = startDate + " 00:00:00.000";
    }
    if (endDate && !endDate.includes(" ")) {
      const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        if (endDate === todayStr) {
          const hours = String(now.getHours()).padStart(2, "0");
          const minutes = String(now.getMinutes()).padStart(2, "0");
          const seconds = String(now.getSeconds()).padStart(2, "0");
          endDate = endDate + ` ${hours}:${minutes}:${seconds}.000`;
        } else {
          endDate = endDate + " 23:59:59.000";
        }
    }

    const convertedStartDate = convertReportToDateTimeLocal(startDate);
    const convertedEndDate = convertReportToDateTimeLocal(endDate);

    setFilterData((prev) => ({
      ...prev,
      startDate: convertedStartDate,
      endDate: convertedEndDate,
    }));
  };

  // Calendar selectable window: last 30 days through today (local dates).
  const minSelectableDate = getLocalDateOffset(30);
  const maxSelectableDate = getLocalDateOffset(0);
  const endDateMin = getReportEndDateMin(filterData.startDate, 30);
  const minSelectableMonth = minSelectableDate.slice(0, 7);
  const maxSelectableMonth = maxSelectableDate.slice(0, 7);

  return (
    <>
      <UserFilter
        modalClose={modalClose}
        onNextClick={(value) => setSelectUsers(value)}
        onApplyFilter={onApplyFilter}
        userModalParams={{ canSelectUsers, ifNotAll: true }}
        showShelfName
      />
      {(!selectUsers.allowed ||
        (selectUsers.allowed &&
          selectUsers.userIds !== "" &&
          selectUsers.next)) && (
        <Modal show={modal} close={modalClose} justify="center">
          <div className="modal-header">
            <div className="d-flex gap-2 align-items-center">
              <h6 className="modal-title d-flex align-items-center fs-22">
                <AppIcon name="filter" />
                {t("commonUi.filter", { ns: "common" })}
              </h6>
              <div className="dropdown filterHeaderDropdown" style={{ scale: "0.9" }}>
                <select
                  className="form-select filterHeaderSelect"
                  value={daySelector}
                  onChange={handleDayChange}
                >
                  <option value="0">{t("commonUi.today", { ns: "common" })}</option>
                  <option value="1">{t("commonUi.yesterday", { ns: "common" })}</option>
                  <option value="3">{t("commonUi.last3Days", { ns: "common" })}</option>
                  <option value="7">{t("commonUi.last7Days", { ns: "common" })}</option>
                  <option value="10">{t("commonUi.last10Days", { ns: "common" })}</option>
                  <option value="15">{t("commonUi.last15Days", { ns: "common" })}</option>
                  <option value="31">{t("commonUi.last31Days", { ns: "common", defaultValue: "Last 31 Days" })}</option>
                </select>
              </div>
            </div>
            <CloseButton theme={"dark"} onClick={modalClose} />
          </div>
          <FormEnter onSubmit={handleFilterSubmit}>
            <div className="modal-body py-2 px-3 bg-white">
              <div className="row filter g-3">
                <div className="col-md-12">
                  <label className="form-label">{t("commonUi.gmt", { ns: "common" })}</label>
                                    <Select
                    options={timeOptions.map((gmt) => ({ id: gmt.value, label: gmt.label }))}
                    value={filterData.gmtOffset}
                    onChange={(value) =>
                      handleFilterDataChange({ target: { id: "gmtOffset", value } })
                    }
                    style={{ fontSize: "14px" }}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{t("commonUi.time", { ns: "common" })}</label>
                  <select
                    className="form-select text-capitalize"
                    onChange={handleTimeChange}
                    value={filterData.time || "Custom"}
                  >
                    <option value="Daily">
                      {t("filter.daily", { defaultValue: "Daily" })}
                    </option>
                    <option value="Monthly">
                      {t("filter.monthly", { defaultValue: "Monthly" })}
                    </option>
                    <option value="Custom">
                      {t("filter.custom", { defaultValue: "Custom" })}
                    </option>
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{t("commonUi.country", { ns: "common" })}</label>
                  <Select
                    placeholder={t("commonUi.selectPlaceholder", { ns: "common" })}
                    value={filterData.country}
                    options={[{ code: "%", name: "ALL" }, ...countries].map(
                      (country) => ({
                        id: country.code,
                        label:
                          country.code === "%"
                            ? t("commonUi.all", { ns: "common" })
                            : country.name,
                      })
                    )}
                    onChange={(value) =>
                      handleFilterDataChange({
                        target: { id: "country", value },
                      })
                    }
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{t("commonUi.sender", { ns: "common" })}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filterData.senderId}
                    id="senderId"
                    onChange={handleFilterDataChange}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">{t("commonUi.status", { ns: "common" })}</label>
                  <select
                    className="form-select text-capitalize"
                    value={filterData.status}
                    id="status"
                    onChange={handleFilterDataChange}
                  >
                    <option value="%">{t("commonUi.all", { ns: "common" })}</option>
                    <option value="Q">
                      {t("filter.status.queued", {
                        defaultValue: "Queued",
                      })}
                    </option>
                    <option value="C">
                      {t("filter.status.processing", {
                        defaultValue: "Processing",
                      })}
                    </option>
                    <option value="P">
                      {t("filter.status.processed", {
                        defaultValue: "Processed",
                      })}
                    </option>
                    <option value="F">
                      {t("filter.status.nonResponding", {
                        defaultValue: "NonResponding",
                      })}
                    </option>
                  </select>
                </div>
                {filterData.time === "Daily" && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label">{t("commonUi.date", { ns: "common" })}</label>
                      <ReportDateTimePicker
                        type="date"
                        className="form-control text-uppercase"
                        id="date"
                        name="date"
                        value={filterData.date}
                        onChange={handleFilterDataChange}
                        required
                        min={minSelectableDate}
                        max={maxSelectableDate}
                      />
                    </div>
                  </div>
                )}
                {filterData.time === "Monthly" && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label">{t("commonUi.month", { ns: "common" })}</label>
                      <ReportDateTimePicker
                        type="month"
                        className="form-control text-capitalize"
                        id="month"
                        name="month"
                        value={filterData.month}
                        onChange={handleFilterDataChange}
                        required
                        min={minSelectableMonth}
                        max={maxSelectableMonth}
                      />
                    </div>
                  </div>
                )}
                {filterData.time === null && (
                  <>
                    <div className="row mb-3">
                      <div className="col-12">
                        <label className="form-label">{t("commonUi.fromDateTime", { ns: "common" })}</label>
                        <ReportDateTimePicker
                          type="datetime-local"
                          className="form-control text-uppercase"
                          id="startDate"
                          name="startDate"
                          value={filterData.startDate}
                          onChange={handleFilterDataChange}
                          required
                          min={minSelectableDate}
                          max={maxSelectableDate}
                        />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-12">
                        <label className="form-label">{t("commonUi.toDateTime", { ns: "common" })}</label>
                        <ReportDateTimePicker
                          type="datetime-local"
                          className="form-control text-uppercase"
                          id="endDate"
                          name="endDate"
                          value={filterData.endDate}
                          onChange={handleFilterDataChange}
                          required
                          min={endDateMin}
                          max={maxSelectableDate}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer d-block bg-white">
              <div className="row g-3">
                <div className="col-6 mt-0">
                  <button
                    type="button"
                    className="btn fs-16 fw-500 br-12 w-100 px-4 py-2 border_lightgray_1 me-2"
                    onClick={handleResetClick}
                  >
                    {t("commonUi.reset", { ns: "common" })}
                  </button>
                </div>
                <div className="col-6 mt-0">
                  <button
                    type="submit"
                    className="cbtn text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                  >
                    {t("commonUi.apply", { ns: "common" })}
                  </button>
                </div>
              </div>
            </div>
          </FormEnter>
        </Modal>
      )}
    </>
  );
}
